import { styled, Table, Typography } from '@mui/joy';
import { css } from '@mui/system';
import dayjs from 'dayjs';

import { SignImageList } from '@/components/processes/SignImageList';
import { I18N_DE } from '@/i18n/de';
import { Sign } from '@/modules/api';
import { useSignCodeQuery } from '@/queries/useSignCodeQuery';

type SignDetailsProps = { sign: Sign };

const StyledTable = styled(Table)(
  ({ theme }) => css`
    margin: ${theme.spacing(1.5)};

    & tbody > tr > td:first-child {
      width: 150px;
    }

    & tbody > tr > td:first-child,
    tbody > tr > td:last-child {
      border: none;
      padding: 4px 6px;
      min-height: ${theme.spacing(3)};
      max-height: ${theme.spacing(4)};
    }

    p {
      font-weight: 400;
      white-space: pre-line;
    }
  `,
);

const KeyTypography = styled(Typography)(
  ({ theme }) => css`
    color: ${theme.palette.text.tertiary};
    font-size: 14px;
  `,
);

const ValueTypography = styled(Typography)(
  ({ theme }) => css`
    color: ${theme.palette.neutral.plainColor};
    font-size: 16px;
    word-wrap: anywhere;
    white-space: wrap;
  `,
);

export function SignDetails({ sign }: SignDetailsProps) {
  const { data: signCode } = useSignCodeQuery(sign.code);

  return (
    <StyledTable>
      <tbody>
        <tr>
          <td>
            <KeyTypography>StVO - Nummer</KeyTypography>
          </td>
          <td>
            <ValueTypography>{sign.code}</ValueTypography>
          </td>
        </tr>
        <tr>
          <td>
            <KeyTypography>Beschreibung</KeyTypography>
          </td>
          <td>
            <ValueTypography>{signCode?.description}</ValueTypography>
          </td>
        </tr>
        {sign.statuses?.map((status) => (
          <tr key={status.id}>
            <td>
              <KeyTypography>{`Status ${I18N_DE.SignStatusType[status.type]}`}</KeyTypography>
            </td>
            <td>
              <ValueTypography>{dayjs(status.createdAt).format('lll')}</ValueTypography>
              <Typography color="neutral" level="body-xs" fontWeight={400}>
                bearbeitet {dayjs(status.updatedAt).format('DD.MM.YYYY HH:mm')}
              </Typography>
            </td>
          </tr>
        ))}
        <tr>
          <td>
            <KeyTypography>Zusatz</KeyTypography>
          </td>
          <td>
            <ValueTypography>{sign.value}</ValueTypography>
          </td>
        </tr>
        <tr>
          <td>
            <KeyTypography>Kategorie</KeyTypography>
          </td>
          <td>
            <ValueTypography>{I18N_DE.SignCategory[sign.category]}</ValueTypography>
          </td>
        </tr>
        <tr>
          <td>
            <KeyTypography>Quelle</KeyTypography>
          </td>
          <td>
            <ValueTypography>{I18N_DE.SignSourceType[sign.source]}</ValueTypography>
          </td>
        </tr>
        <tr>
          <td>
            <KeyTypography>Größe</KeyTypography>
          </td>
          <td>
            <ValueTypography>{sign.size ? I18N_DE.SignSize[sign.size] : 'Unbekannt'}</ValueTypography>
          </td>
        </tr>
        <tr>
          <td>
            <KeyTypography>Reflektionsklasse</KeyTypography>
          </td>
          <td>
            <ValueTypography>
              {sign.reflectionClass ? I18N_DE.SignReflectionClass[sign.reflectionClass] : 'Unbekannt'}
            </ValueTypography>
          </td>
        </tr>
        <tr>
          <td>
            <KeyTypography>Beschilderungsart</KeyTypography>
          </td>
          <td>
            <ValueTypography>{sign.permanency ? I18N_DE.SignPermanency[sign.permanency] : 'Unbekannt'}</ValueTypography>
          </td>
        </tr>
        <tr>
          <td>
            <KeyTypography>Zustand</KeyTypography>
          </td>
          <td>
            <ValueTypography>{sign.condition ? I18N_DE.SignCondition[sign.condition] : 'Unbekannt'}</ValueTypography>
          </td>
        </tr>
        <tr>
          <td>
            <KeyTypography>Bilder</KeyTypography>
          </td>
          <td>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control, jsx-a11y/label-has-for */}
            <SignImageList signId={sign.id} images={sign.images || []} disableDelete />
          </td>
        </tr>
      </tbody>
    </StyledTable>
  );
}
